import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiCalendarBlank, PiChatCircleDots, PiArrowRightBold } from "react-icons/pi";
import logger from "@/utils/logger";

interface BlogPost {
  title: string;
  date: string;
  comments: string;
  image: string;
  url?: string;
}

export default function HokBlog({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#684DF4";
  const secondaryColor = content.colorPalette?.secondary || "#8a2be2";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const defaultPosts: BlogPost[] = [
    {
      title: "10 Tips for Effective Online Learning",
      date: "25 March, 2026",
      comments: "2",
      image: "/assets/img/blog/blog_1_1.png",
      url: "blog-details.html",
    },
    {
      title: "How to Choose the Right Course for Your Career",
      date: "19 March, 2026",
      comments: "7",
      image: "/assets/img/blog/blog_1_2.png",
      url: "blog-details.html",
    },
    {
      title: "The Future of E-Learning: Trends to Watch in 2026",
      date: "20 March, 2026",
      comments: "10",
      image: "/assets/img/blog/blog_1_3.png",
      url: "blog-details.html",
    },
    {
      title: "Building a Personal Brand as an Online Instructor",
      date: "22 March, 2026",
      comments: "12",
      image: "/assets/img/blog/blog_1_4.png",
      url: "blog-details.html",
    },
  ];

  const posts: BlogPost[] = content.posts || defaultPosts;

  const updatePost = (idx: number, field: keyof BlogPost, value: string) => {
    if (!onContentChange) return;
    const newPosts = [...posts];
    newPosts[idx] = { ...newPosts[idx], [field]: value };
    onContentChange({ ...content, posts: newPosts });
  };

  const addPost = () => {
    if (!onContentChange) return;
    const newPosts = [...posts, {
      title: "New Article Title",
      date: "01 January, 2026",
      comments: "0",
      image: "/assets/img/blog/blog_1_1.jpg",
      url: "blog-details.html",
    }];
    onContentChange({ ...content, posts: newPosts });
  };

  const removePost = (idx: number) => {
    if (!onContentChange) return;
    const newPosts = posts.filter((_, i) => i !== idx);
    onContentChange({ ...content, posts: newPosts });
  };

  return (
    <section
      className={`relative overflow-hidden w-full py-10 sm:py-14 bg-white ${
        isSelected ? "ring-2 ring-blue-500 relative z-20" : ""
      }`}
      style={{ fontFamily }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Section header */}
        <div className="row justify-content-center mb-8">
          <div className="col-lg-8 col-xxl-6 text-center">
            <span
              className="inline-block text-xs sm:text-sm font-bold tracking-wider uppercase mb-3"
              style={{ color: primaryColor }}
            >
              <EditableText
                value={content.subtitle || "Learning Hub"}
                onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                as="span"
                placeholder="Subtitle"
              />
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
              <EditableText
                value={content.title || "Latest Articles & Resources"}
                onChange={(val) => onContentChange?.({ ...content, title: val })}
                as="span"
                placeholder="Title"
              />
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              <EditableText
                value={content.description || "Your go-to resource for learning tips, industry insights, and expert advice on online education."}
                onChange={(val) => onContentChange?.({ ...content, description: val })}
                as="span"
                placeholder="Description"
              />
            </p>
          </div>
        </div>

        {/* Blog posts grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post, idx) => (
            <div
              key={idx}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              {isSelected && (
                <button
                  onClick={() => removePost(idx)}
                  className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm font-bold"
                  title="Remove Post"
                >
                  ×
                </button>
              )}

              {/* Post image */}
              <div className="relative overflow-hidden h-48 group/img">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {isSelected && (
                  <label className="absolute top-3 left-3 z-10 flex items-center justify-center p-2 bg-white/90 backdrop-blur-xs border border-gray-200 rounded-lg hover:bg-white cursor-pointer transition-all shadow-sm hover:scale-105">
                    <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const toast = (await import("react-hot-toast")).default;
                          const homepageApi = (await import("@/services/homepageApi")).default;
                          const toastId = toast.loading("Uploading image...");
                          try {
                            const response = await homepageApi.uploadImage(file);
                            const url = response.data?.url || response.url;
                            if (url) {
                              updatePost(idx, "image", url);
                              toast.success("Image uploaded successfully!", { id: toastId });
                            } else {
                              toast.error("Failed to upload.", { id: toastId });
                            }
                          } catch (err) {
                            toast.error("Upload failed", { id: toastId });
                            logger.error(err);
                          }
                        }
                      }}
                    />
                  </label>
                )}
                {/* Date badge overlay */}
                <div
                  className="absolute bottom-3 left-3 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                >
                  <EditableText
                    value={post.date}
                    onChange={(val) => updatePost(idx, "date", val)}
                    as="span"
                    placeholder="Date"
                  />
                </div>
              </div>

              {/* Post content */}
              <div className="p-5">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 line-clamp-2 leading-snug">
                  <EditableText
                    value={post.title}
                    onChange={(val) => updatePost(idx, "title", val)}
                    as="span"
                    placeholder="Post Title"
                  />
                </h3>

                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-gray-500 text-sm">
                    <PiChatCircleDots className="w-4 h-4" />
                    <EditableText
                      value={`Comments (${post.comments})`}
                      onChange={(val) => {
                        const match = val.match(/Comments \((\d+)\)/);
                        updatePost(idx, "comments", match ? match[1] : "0");
                      }}
                      as="span"
                      placeholder="Comments (0)"
                    />
                  </span>

                  <a
                    href={post.url || "#"}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors hover:gap-2.5"
                    style={{ color: primaryColor }}
                  >
                    Read More
                    <PiArrowRightBold className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          ))}

          {isSelected && (
            <button
              onClick={addPost}
              className="flex flex-col items-center justify-center min-h-[320px] rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all"
            >
              <span className="text-4xl mb-2">+</span>
              <span className="text-sm font-semibold">Add Article</span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
